import React, { useEffect, useState } from "react";
import { NavLink, useParams, useSearchParams } from "react-router-dom";
import { getGiftCardStatus } from "../../api/giftcard";
import useWindowDimensions from "@helpers/hooks/windowDemensions";
import HeartLoader from "@shared/HeartLoader";
import SuccessReveal from "../../UploadImage/SuccessReveal";
import "../../UploadImage/SuccessScene.css";
import "./giftsuccess.css";

const TABLE = "/assets/images/success/table-top.webp";
const DUKI = "/mascot/poses/00-celebrate-master.webp";

// Card art: same tier mapping as the purchase page — custom amounts resolve to
// their theme range's representative baked card.
const BAKED = { 5: 1, 8: 1, 12: 1, 16: 1, 24: 1, 36: 1 };
const THEME_BUNDLE = { "gc-rose": 5, "gc-emerald": 8, "gc-green": 12, "gc-pearl": 16, "gc-onyx": 24, "gc-paper": 36 };
const themeFor = (n) => {
  if (n >= 30) return "gc-paper";
  if (n >= 20) return "gc-onyx";
  if (n >= 16) return "gc-pearl";
  if (n >= 12) return "gc-green";
  if (n >= 8) return "gc-emerald";
  return "gc-rose";
};
const cardArtFor = (frames) =>
  `/assets/images/giftcard/card-tier-${BAKED[frames] ? frames : THEME_BUNDLE[themeFor(frames)]}.webp`;

const BubbleIcon = () => (<svg viewBox="0 0 24 24"><path d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5c-1.55 0-3-.41-4.26-1.13L3.5 20l1.18-4.5A8.5 8.5 0 1 1 21 11.5z" /><path d="M8.5 10.5h7M8.5 13.5h4.5" /></svg>);
const CalIcon = () => (<svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>);
const UserIcon = () => (<svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>);
const GiftIcon = () => (<svg viewBox="0 0 24 24"><rect x="3" y="8" width="18" height="12" rx="2.5" /><line x1="12" y1="8" x2="12" y2="20" /><path d="M12 8c0-2.2-1-3.5-2.6-3.5-1.1 0-1.8.8-1.8 1.7C7.6 7.4 9.2 8 12 8Z" /><path d="M12 8c0-2.2 1-3.5 2.6-3.5 1.1 0 1.8.8 1.8 1.7C16.4 7.4 14.8 8 12 8Z" /></svg>);

const Duki = ({ className }) => (
  <div className={className}>
    <span className="ss-legshadow" />
    <img src={DUKI} alt="דוכי" />
  </div>
);

const GiftCardArt = ({ frames }) => (
  <div className="gs-card">
    <img src={cardArtFor(frames)} alt="" loading="lazy" />
  </div>
);

const Steps = ({ sent }) => (
  <div className="ss-steps">
    <div className="ss-step on"><span className="ss-seg" /><span className="ss-lbl">שולם</span></div>
    <div className={`ss-step${sent ? " on" : ""}`}><span className="ss-seg" /><span className="ss-lbl">נשלח ב‑SMS</span></div>
    <div className="ss-step"><span className="ss-seg" /><span className="ss-lbl">מימוש באתר</span></div>
  </div>
);

function Details({ info, centered }) {
  const frames = info.framesTotal || 12;
  // The BUYER's code goes out at activation regardless of recipient scheduling
  // (deliveryStatus tracks the recipient leg only).
  const sent = info.status === "ACTIVE" || info.deliveryStatus === "sent";
  const scheduled = !!info.scheduledSendAt && info.deliveryStatus !== "sent";
  const schedDate = scheduled
    ? new Date(info.scheduledSendAt).toLocaleDateString("he-IL", { day: "numeric", month: "numeric" })
    : "";
  const recipientLabel = info.recipientName || "המקבל/ת";

  return (
    <>
      <div style={centered ? { textAlign: "center" } : null}>
        <div className="ss-eyebrow">התשלום אושר</div>
        <h1 className="ss-h1">המתנה נקלטה בהצלחה</h1>
      </div>

      <div style={{ marginTop: 22 }}><Steps sent={sent} /></div>

      <div className="ss-row" style={{ marginTop: 10 }}>
        <div className="ss-ic"><BubbleIcon /></div>
        <div>
          <div className="ss-k">קוד המימוש</div>
          <div className="ss-v">{sent ? `נשלח אליך ב‑SMS${info.hasEmail ? " ולמייל" : ""}` : "בדרך אליך ב‑SMS"}</div>
          {info.hasRecipient && !scheduled && (
            <div className="ss-sm">נשלח גם ל{recipientLabel}{info.recipientPhone ? ` · ${info.recipientPhone}` : ""}</div>
          )}
        </div>
      </div>

      {scheduled && (
        <div className="ss-row">
          <div className="ss-ic"><CalIcon /></div>
          <div>
            <div className="ss-k">שליחה למקבל/ת</div>
            <div className="ss-v">יישלח ל{recipientLabel} ב‑{schedDate}</div>
          </div>
        </div>
      )}

      {(info.buyerName || info.buyerPhone) && (
        <div className="ss-row">
          <div className="ss-ic"><UserIcon /></div>
          <div>
            <div className="ss-k">פרטי הרוכש/ת</div>
            <div className="ss-v">
              {info.buyerName}
              {info.buyerPhone ? <>{" · "}<span dir="ltr">{info.buyerPhone}</span></> : null}
            </div>
            {info.buyerEmail && <div className="ss-sm" dir="ltr">{info.buyerEmail}</div>}
          </div>
        </div>
      )}

      {info.hasRecipient && (
        <div className="ss-row">
          <div className="ss-ic"><GiftIcon /></div>
          <div>
            <div className="ss-k">מקבל/ת המתנה</div>
            <div className="ss-v">
              {info.recipientName || "לפי הטלפון שהוזן"}
              {info.recipientPhone ? <>{" · "}<span dir="ltr">{info.recipientPhone}</span></> : null}
            </div>
            {info.recipientEmail && <div className="ss-sm" dir="ltr">{info.recipientEmail}</div>}
          </div>
        </div>
      )}

      <div className="ss-product">
        <div className="gs-thumb"><img src={cardArtFor(frames)} alt="" loading="lazy" /></div>
        <div>
          <div className="ss-pname">גיפט קארד בלנדס</div>
          <div className="ss-pmeta">תקף לשנה</div>
        </div>
        <div className="ss-count">×{frames}</div>
      </div>

      <div className="ss-actions">
        <NavLink className="ss-btn" to="/gift-card">לשליחת עוד מתנה</NavLink>
        <NavLink className="ss-btn ghost" to="/">לדף הבית</NavLink>
      </div>
      <div className="gs-note">ההודעה לא הגיעה? לרוב היא נוחתת תוך דקות ספורות</div>
      {info.oid && (
        <div className="gs-ref">מס׳ הזמנה: <span dir="ltr">{info.oid}</span></div>
      )}
    </>
  );
}

/**
 * GiftSuccess — the gift-card purchase confirmation, mirroring the normal
 * order success page (reveal, table scene, mascot, details panel) with
 * gift-relevant content. The redemption code itself is never shown here
 * (non-sensitive status endpoint) — it arrives by SMS/email.
 */
const GiftSuccess = () => {
  const { id } = useParams(); // giftOrderId (GC-...) or "preview" in dev
  const [searchParams] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  // Dev-only preview so the page is reviewable without paying:
  // /gift-success/preview?frames=24&recipient=נועה&scheduled=1&email=1
  const isPreview = process.env.NODE_ENV !== "production" && id === "preview";

  useEffect(() => {
    window.scrollTo(0, 0);

    if (isPreview) {
      const recipient = searchParams.get("recipient") || "";
      const scheduled = searchParams.get("scheduled") === "1";
      setInfo({
        success: true,
        status: "ACTIVE",
        framesTotal: Number(searchParams.get("frames")) || 12,
        oid: 534416624,
        buyerName: "אבא ואמא",
        buyerPhone: "0521236789",
        buyerEmail: searchParams.get("email") === "1" ? "sk•••@gmail.com" : "",
        recipientName: recipient,
        recipientPhone: recipient ? "0521234567" : "",
        recipientEmail: recipient ? "no•••@gmail.com" : "",
        hasRecipient: !!recipient,
        hasEmail: searchParams.get("email") === "1",
        deliveryStatus: scheduled ? "pending" : "sent",
        scheduledSendAt: scheduled ? new Date(Date.now() + 3 * 864e5).toISOString() : null,
      });
      setLoading(false);
      return;
    }

    let alive = true;
    getGiftCardStatus(id)
      .then((res) => alive && setInfo(res))
      .catch(() => {})
      .finally(() => alive && setLoading(false));

    // drop the GC- watch id once we've landed (the Bit modal handles its own
    // close via the location morph — don't touch the secondary modals here)
    const t = setTimeout(() => {
      const oid = localStorage.getItem("orderId");
      if (oid && oid.startsWith("GC-")) localStorage.removeItem("orderId");
    }, 1000);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const data = info && info.success ? info : { framesTotal: 12, deliveryStatus: "sent" };

  return (
    <>
      <SuccessReveal />

      {!isLoading && (
        <div className="ss-wrap gs-wrap">
          {isMobile ? (
            <div className="ss-mobile">
              <div className="ss-scene">
                <img className="ss-table" src={TABLE} alt="" />
                <GiftCardArt frames={data.framesTotal} />
                <div className="ss-vig" />
              </div>
              <Duki className="ss-mascot" />
              <div className="ss-details"><Details info={data} centered /></div>
            </div>
          ) : (
            <div className="ss-desktop">
              <section className="ss-scenary">
                <img className="ss-table" src={TABLE} alt="" />
                <div className="ss-cluster"><GiftCardArt frames={data.framesTotal} /></div>
                <div className="ss-vig" />
                <div className="ss-fade" />
              </section>
              <section className="ss-panel"><div className="ss-content"><Details info={data} /></div></section>
              <Duki className="ss-duki" />
            </div>
          )}
        </div>
      )}

      <section className="footer-btm xy">
        <p style={{ lineHeight: "36px", margin: "0" }}>Copyright Blends 2021-{new Date().getFullYear()} &copy;</p>
        <div className="btm-wrap">
          <a href="#" title="leads to a floating screen 2" data-target="#float2" data-toggle="modal">מדיניות פרטיות</a>
          <a href="#" title="leads to a floating screen 3" data-target="#float3" data-toggle="modal">תנאי שימוש</a>
        </div>
      </section>
      <HeartLoader isLoading={isLoading} showText={false} />
    </>
  );
};

export default GiftSuccess;
